import { useQuery } from "@apollo/client";
import { ResultOf } from "@graphql-typed-document-node/core";
import { groupBy } from "lodash";

import { gql } from "~/__generated__";
import { parseDateToString } from "~/modules/app/util/dateHelpers";
import useCurrentUser from "~/modules/auth/hooks/useCurrentUser";

import { FeedbackListItemProps } from "./FeedbackListItem";

const ReportsHavingFeedbackQuery = gql(`
  query ReportsHavingFeedbackQuery($filters: GqlPlannerVisitWorkerReportFilters!) {
    plannerVisitWorkerReportGetAll(filters: $filters) {
      id
      plannerShift {
        id
        startTime
        endTime
      }
      plannerVisit {
        id
        plannerArea {
          id
          name
        }
        protocol {
          id
          name
        }
      }
      numberOfFeedbackComments
    }
  }
`);

export function useReportsHavingFeedback() {
    const user = useCurrentUser();
    if (user === undefined) throw new Error("User is not defined");

    const { data, loading, error } = useQuery(ReportsHavingFeedbackQuery, {
        variables: {
            filters: { hasFeedback: true, employeeId: user.id },
        },
    });

    return { loading, error, data: mapGraphqlToListElements(data?.plannerVisitWorkerReportGetAll ?? []) };
}

type UseReportsHavingFeedbackShape = ResultOf<typeof ReportsHavingFeedbackQuery>["plannerVisitWorkerReportGetAll"];
function mapGraphqlToListElements(input: UseReportsHavingFeedbackShape): Array<FeedbackListItemProps["data"]> {
    const reportsPerShift = groupBy(input, x => x.plannerShift?.id);

    return Object.entries(reportsPerShift).map(([_, reports]) => {
        const report0 = reports[0];

        return {
            area: report0.plannerVisit?.plannerArea?.name ?? "Unknown area",
            endAt: report0.plannerShift?.endTime ?? parseDateToString(new Date()),
            startAt: report0.plannerShift?.startTime ?? parseDateToString(new Date()),
            reports: reports.map(report => ({
                id: report.id,
                protocol: report.plannerVisit?.protocol?.name ?? "Unknown protocol",
                numberOfComments: report.numberOfFeedbackComments,
            })),
        };
    });
}
